import React, { useMemo } from 'react';
import { RotateCcwIcon } from 'lucide-react';
import type { SourceFilter } from '../../types/dashboard';
import { useFilters } from '../../contexts/FilterContext';
import { territories } from '../../data/territories';
import { PROGRAM_TYPES, PROGRAM_YEARS, SEASONS, SPORTS } from '../../data/dimensions';
import { coachMatches, resolveTerritoryIds } from '../../utils/filters';
import { resolveRange } from '../../utils/calendar';
import { AD_NAMES, FBC_NAMES } from '../../utils/territory';
import { FilterDropdown, type DropdownOption } from '../ui/FilterDropdown';
import { InfoTip } from '../ui/InfoTip';
import { DateRangeDropdown } from './DateRangeDropdown';
import { useMetricParam } from '../../hooks/useMetricParam';
import { metricMeta } from '../../utils/metricConfig';
export type FilterMode = 'full' | 'retention' | 'entity';
const toOptions = (list: string[]): DropdownOption[] => list.map(v => ({
  value: v,
  label: v
}));
export function FilterBar({
  mode
}: {
  mode: FilterMode;
}) {
  const {
    filters,
    update,
    reset,
    isDefault
  } = useFilters();
  const [metric] = useMetricParam();
  const retention = mode === 'retention';
  const entity = mode === 'entity';
  /** New-member counts have no program attribution, so program filters cannot apply to them. */
  const memberMetric = metric === 'members';
  const programReason = 'Not applied to New Members: a member is only linked to a program through a qualifying registration. Applies to Registrations, Conversion Rate, Revenue, Registrations per Member and Retention.';
  const territoryOptions = useMemo(() => territories.filter(t => coachMatches(t, filters.coach)).map(t => ({
    value: t.id,
    label: `${t.name}, ${t.state}`
  })), [filters.coach]);
  const territoryIds = useMemo(() => resolveTerritoryIds(filters), [filters]);
  const aopOptions = useMemo(() => territories.filter(t => territoryIds.includes(t.id)).flatMap(t => t.aops.map(a => ({
    value: a.id,
    label: a.name,
    group: t.name
  }))), [territoryIds]);
  const coachOptions: DropdownOption[] = [...FBC_NAMES.map(n => ({
    value: `fbc:${n}`,
    label: n,
    group: 'Franchise Business Coach'
  })), ...AD_NAMES.map(n => ({
    value: `ad:${n}`,
    label: n,
    group: 'Area Developer'
  }))];
  const range = resolveRange(filters);
  const entityReason = 'Set by the selected territory / AOP';
  const retentionReason = 'Retention uses season cohort selectors, not the acquisition date range';
  const scopeText = `${territoryIds.length} of ${territories.length} territories · ${filters.aops.length ? `${filters.aops.length} AOPs` : 'All AOPs'}`;
  const context = retention ? `Season cohorts · ${scopeText} · date range, source, season & year not applied` : entity ? `Acquisition date range ${range.label} · territory & AOP set by drilldown` : `Acquisition date range ${range.label} · ${scopeText} · ${filters.source === 'All' ? 'All sources' : `${filters.source} only`} · Oct ’26 month-to-date excluded`;
  return <div className="sticky top-0 z-30 border-b border-line bg-white px-4 py-2.5 md:px-6" data-id="element-231" data-mp="1s1q1ct9zn7do">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-2" data-id="element-232" data-mp="1x35kugwip5ty">
        <div className="flex flex-wrap items-center gap-2" data-id="element-233" data-mp="1j34jz1g95yeh">
          <span className="w-[128px] shrink-0 text-xs font-semibold text-navy" data-id="element-234" data-mp="1j4o4x5cusxml">Scope</span>
          <DateRangeDropdown disabled={retention} disabledReason={retentionReason} data-id="element-235" data-mp="1o1xxgtvjhpen" />
          <FilterDropdown label="Territory" options={territoryOptions} value={filters.territories} onChange={v => update({
          territories: v
        })} multiple searchable allLabel="All territories" disabled={entity} disabledReason={entityReason} menuWidth={260} data-id="element-236" data-mp="2brsapcjrtfzf" />
          <FilterDropdown label="AOP" options={aopOptions} value={filters.aops} onChange={v => update({
          aops: v
        })} multiple searchable allLabel="All AOPs" disabled={entity} disabledReason={entityReason} menuWidth={260} data-id="element-237" data-mp="2upcptrwqh06u" />
          <FilterDropdown label="FBC / AD" options={coachOptions} value={filters.coach === 'all' ? [] : [filters.coach]} onChange={v => update({
          coach: v[0] ?? 'all'
        })} allLabel="All" disabled={entity} disabledReason={entityReason} menuWidth={240} data-id="element-238" data-mp="11uby9tb96k9v" />
          <FilterDropdown label="Source" options={toOptions(['Digital', 'Local'])} value={filters.source === 'All' ? [] : [filters.source]} onChange={v => update({
          source: (v[0] ?? 'All') as SourceFilter
        })} allLabel="All" disabled={retention} disabledReason="Acquisition source does not apply to participant retention" menuWidth={180} data-id="element-239" data-mp="1pkg1hzkj4i0c" />
          <button type="button" onClick={reset} disabled={isDefault} className="ml-auto inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-xs font-semibold text-accent-strong transition-colors duration-150 hover:bg-accent-soft disabled:cursor-default disabled:text-ink-subtle disabled:hover:bg-transparent" data-id="element-240" data-mp="2fu6t33g8rtwi">
            <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-241" data-mp="2yt2ky84mnw76" />
            Reset filters
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-242" data-mp="33rcgiju9kytb">
          <span className="flex w-[128px] shrink-0 items-center gap-1 text-xs font-semibold text-navy" data-id="element-243" data-mp="33psyoosymfpf">
            Program filters
            <InfoTip align="left" width={330} content={<>
                  <b data-id="element-245" data-mp="3hkbw0lu1qt0v">Apply to member reporting:</b> acquisition date range, territory, AOP, FBC / AD and source.
                  <br data-id="element-246" data-mp="3mj5c8m0pwy4q" />
                  <b data-id="element-247" data-mp="3min8z5fn1jnb">Apply to registration-derived measures only:</b> sport, program type, program season and program year narrow registrations, revenue, converted members and retention participants.
                  <br data-id="element-248" data-mp="1igddxeb3m7oc" />
                  {memberMetric ? 'They are disabled on New Members because no attribution rule links a member to a program before they register.' : retention ? 'On Retention, program season and year are chosen in the cohort controls instead.' : `Currently applied to ${metricMeta(metric).label}.`}
                </>} data-id="element-244" data-mp="3dhfgx53ptf1o" />
          </span>
          <FilterDropdown label="Sport" options={toOptions(SPORTS)} value={filters.sports} onChange={v => update({
          sports: v
        })} multiple disabled={memberMetric} disabledReason={programReason} data-id="element-249" data-mp="2f3qjlmfazev1" />
          <FilterDropdown label="Program type" options={toOptions(PROGRAM_TYPES)} value={filters.programs} onChange={v => update({
          programs: v
        })} multiple disabled={memberMetric} disabledReason={programReason} data-id="element-250" data-mp="fsh2l0yluntp" />
          <FilterDropdown label="Program season" options={toOptions(SEASONS)} value={filters.seasons} onChange={v => update({
          seasons: v
        })} multiple disabled={retention || memberMetric} disabledReason={memberMetric ? programReason : 'Retention selects seasons in its own cohort controls'} data-id="element-251" data-mp="2ilo113hgqg8q" />
          <FilterDropdown label="Program year" options={toOptions(PROGRAM_YEARS)} value={filters.years} onChange={v => update({
          years: v
        })} multiple disabled={retention || memberMetric} disabledReason={memberMetric ? programReason : 'Retention selects seasons in its own cohort controls'} menuWidth={180} data-id="element-252" data-mp="1zomsx9sm400k" />
          <p className="ml-auto text-[11.5px] text-ink-muted" data-id="element-253" data-mp="2lst08z1ewlg6">{context}</p>
        </div>
      </div>
    </div>;
}